import React, { useMemo } from "react";
import DateRangePresets from "../../../features/filter/TabFilterModal/DateRangePresets";
import DateRangeFilterControl from "../../../components/common/FilterControls/DateRangeFilterControl";
import ActivityNodeFilterControl from "../../../components/common/FilterControls/ActivityNodeFilterControl";
import ObjectAttributeFilterControl from "../../../components/common/FilterControls/ObjectAttributeFilterControl";

/**
 * ReferenceLineSettings - Average/Median reference line configuration.
 *
 * Fully independent data basis (own object/event type + attribute, own
 * reference period, own activity-node/object-attribute filters) — isolated
 * from the tile's own local filters and the global/tab filter. Never reads
 * or writes localFilters; all state lives in the single `referenceLine`
 * object passed down from the hook.
 */
const ReferenceLineSettings = ({
  referenceLine,
  onChange,
  objectTypes,
  eventTypes,
  objectTypeNumericAttributes,
  eventTypeNumericAttributes,
  activeFile,
  loading = false,
}) => {
  const update = (patch) => onChange({ ...referenceLine, ...patch });

  const availableAttributes = useMemo(() => {
    if (referenceLine.attributeSource === "object") {
      return referenceLine.objectType
        ? objectTypeNumericAttributes[referenceLine.objectType] || []
        : [];
    }
    return referenceLine.eventType
      ? eventTypeNumericAttributes[referenceLine.eventType] || []
      : [];
  }, [
    referenceLine.attributeSource,
    referenceLine.objectType,
    referenceLine.eventType,
    objectTypeNumericAttributes,
    eventTypeNumericAttributes,
  ]);

  // DateRangePresets/DateRangeFilterControl both operate on a {startDate, endDate,
  // enabled} shape (see LocalFilterPanel's own dateRange section) — the reference
  // line stores periodStart/periodEnd directly (matching the backend's field
  // names), so this is a thin adapter; the "enabled" the controls emit is not
  // meaningful here (the reference line's own top-level toggle already gates
  // everything) and is intentionally discarded.
  const dateRangeValue = {
    startDate: referenceLine.periodStart,
    endDate: referenceLine.periodEnd,
    enabled: true,
  };
  const handleDateRangeChange = (v) => {
    // Preset buttons return full ISO timestamps (see berlinDate.js getPresetRange);
    // periodStart/periodEnd are validated backend-side as bare YYYY-MM-DD, so
    // truncate here — mirrors AttributeSumLineChart.jsx's own
    // handleDateRangePresetChange, which does the same for the main chart's date range.
    update({
      periodStart: (v.startDate || "").slice(0, 10) || null,
      periodEnd: (v.endDate || "").slice(0, 10) || null,
    });
  };

  return (
    <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 space-y-4">
      <div className="flex items-center justify-between">
        <label className="block text-sm font-medium text-gray-700">
          Average / Median Reference Line
        </label>
        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={referenceLine.enabled}
            onChange={(e) => update({ enabled: e.target.checked })}
            className="h-4 w-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
            style={{ accentColor: "#0066cc" }}
            disabled={loading}
          />
          <span className="text-sm text-gray-600">Enabled</span>
        </label>
      </div>

      {referenceLine.enabled && (
        <>
          <p className="text-xs text-gray-500">
            Fully independent data basis — its own type, attribute, reference period
            and filters, isolated from this tile's local filters and the global/tab filter.
          </p>

          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">
              Line Type
            </label>
            <select
              value={referenceLine.type}
              onChange={(e) => update({ type: e.target.value })}
              className="w-full px-3 py-2 bg-white border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
              disabled={loading}
            >
              <option value="average">Average</option>
              <option value="median">Median</option>
            </select>
          </div>

          <div className="flex gap-4">
            <label className="inline-flex items-center gap-2 cursor-pointer">
              <input
                type="radio"
                name="referenceLineAttributeSource"
                checked={referenceLine.attributeSource === "object"}
                onChange={() => update({ attributeSource: "object" })}
                className="focus:ring-primary-500"
                style={{ accentColor: "#0066cc" }}
                disabled={loading}
              />
              <span className="text-sm text-gray-700">Object Attribute</span>
            </label>
            <label className="inline-flex items-center gap-2 cursor-pointer">
              <input
                type="radio"
                name="referenceLineAttributeSource"
                checked={referenceLine.attributeSource === "event"}
                onChange={() => update({ attributeSource: "event" })}
                className="focus:ring-primary-500"
                style={{ accentColor: "#0066cc" }}
                disabled={loading}
              />
              <span className="text-sm text-gray-700">Event Attribute</span>
            </label>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {referenceLine.attributeSource === "object" ? (
              <>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">
                    Object Type
                  </label>
                  <select
                    value={referenceLine.objectType || ""}
                    onChange={(e) =>
                      update({ objectType: e.target.value || null, objectAttribute: null })
                    }
                    className="w-full px-3 py-2 bg-white border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                    disabled={loading || (objectTypes || []).length === 0}
                  >
                    <option value="">Select object type...</option>
                    {(objectTypes || []).map((t) => (
                      <option key={t} value={t}>
                        {t}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">
                    Object Attribute
                  </label>
                  <select
                    value={referenceLine.objectAttribute || ""}
                    onChange={(e) => update({ objectAttribute: e.target.value || null })}
                    className="w-full px-3 py-2 bg-white border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                    disabled={loading || !referenceLine.objectType || availableAttributes.length === 0}
                  >
                    <option value="">Select attribute...</option>
                    {availableAttributes.map((a) => (
                      <option key={a} value={a}>
                        {a}
                      </option>
                    ))}
                  </select>
                </div>
              </>
            ) : (
              <>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">
                    Event Type
                  </label>
                  <select
                    value={referenceLine.eventType || ""}
                    onChange={(e) =>
                      update({ eventType: e.target.value || null, eventAttribute: null })
                    }
                    className="w-full px-3 py-2 bg-white border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                    disabled={loading || (eventTypes || []).length === 0}
                  >
                    <option value="">Select event type...</option>
                    {(eventTypes || []).map((t) => (
                      <option key={t} value={t}>
                        {t}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">
                    Event Attribute
                  </label>
                  <select
                    value={referenceLine.eventAttribute || ""}
                    onChange={(e) => update({ eventAttribute: e.target.value || null })}
                    className="w-full px-3 py-2 bg-white border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                    disabled={loading || !referenceLine.eventType || availableAttributes.length === 0}
                  >
                    <option value="">Select attribute...</option>
                    {availableAttributes.map((a) => (
                      <option key={a} value={a}>
                        {a}
                      </option>
                    ))}
                  </select>
                </div>
              </>
            )}
          </div>

          <div className="space-y-2">
            <label className="block text-xs font-medium text-gray-600">
              Reference Period
            </label>
            <DateRangePresets value={dateRangeValue} onChange={handleDateRangeChange} />
            <DateRangeFilterControl
              value={dateRangeValue}
              onChange={handleDateRangeChange}
              activeFile={activeFile}
              disabled={loading}
            />
          </div>

          <ActivityNodeFilterControl
            value={referenceLine.activityNodeFilters}
            onChange={(v) => update({ activityNodeFilters: v })}
            activeFile={activeFile}
            disabled={loading}
          />

          <ObjectAttributeFilterControl
            value={referenceLine.objectAttributeFilters}
            onChange={(v) => update({ objectAttributeFilters: v })}
            activeFile={activeFile}
            disabled={loading}
          />

          <div>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={referenceLine.countNonWorkingDays}
                onChange={(e) => update({ countNonWorkingDays: e.target.checked })}
                className="h-4 w-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                style={{ accentColor: "#0066cc" }}
                disabled={loading}
              />
              <span className="text-sm text-gray-700">Count non-working days</span>
            </label>
            <p className="text-xs text-gray-500 mt-1">
              Off (default): only days with actual data count toward the average/median and
              the cumulative line's stepping. On: every calendar day counts, even with no data.
            </p>
          </div>
        </>
      )}
    </div>
  );
};

export default ReferenceLineSettings;
